import classNames from 'classnames';
import { useFormikContext } from 'formik';
import { useMemo } from 'react';
import { Flex, Heading, List } from 'yoso';
import { BaseCard } from '~/components/Card';
import { useEvolution } from '~/components/Evolve/Evolve.hooks';
import { EvolveValues } from '~/components/Evolve/Form/EvolveForm.hooks';
import { EvolveSelectedFriendCard } from '~/components/Evolve/Selected/EvolveSelectedFriendCard';
import { T } from '~/components/I18n';
import { PlusIcon } from '~/components/Icon';
import { AdditionalNftModel, useFriendsMap } from '~/components/Membership/Membership.hooks';
import { formatBoost } from '~/components/Util/number';
import styles from './EvolveSelectedFriends.module.scss';

export const titleByActiveCard: Record<number, string> = {
  [-1]: 'citizen_evolve_popup_choice_all_title',
  0: 'citizen_evolve_popup_choice_title1',
  1: 'citizen_evolve_popup_choice_title2',
  2: 'citizen_evolve_popup_choice_title3',
};

export const descriptionByActiveCard: Record<number, string> = {
  [-1]: 'citizen_evolve_popup_choice_all_desc',
  0: 'citizen_evolve_popup_choice_sub1',
  1: 'citizen_evolve_popup_choice_sub2',
  2: 'citizen_evolve_popup_choice_sub3',
};

export type EvolveSelectedFriendsProps = {
  name?: string;
  className?: string;
};

export const EvolveSelectedFriends = ({ className }: EvolveSelectedFriendsProps) => {
  const {
    values: { tokenIdList = [], tokenType = '' },
  } = useFormikContext<EvolveValues>();
  const { data: friend } = useEvolution(tokenType);
  const { data: friendsMap = {} } = useFriendsMap();
  const activeCard = useMemo(() => tokenIdList.indexOf(undefined), [tokenIdList]);
  const total = useMemo(
    () =>
      tokenIdList
        .map((id) => id && friendsMap[id])
        .filter(Boolean)
        .reduce((acc, friend) => acc + Number((friend as AdditionalNftModel).meta['Boost']), 0),
    [friendsMap, tokenIdList]
  );
  return (
    <BaseCard className={classNames(styles.root, className)} shadow>
      <div className={styles.header}>
        <Heading level={4} className={styles.title}>
          <T name={titleByActiveCard[activeCard]} />
        </Heading>
        <p className={styles.description}>
          <T
            name={descriptionByActiveCard[activeCard]}
            options={[
              friend?.evolvedGrowth || '',
              <strong key={total} className={styles.strong}>
                {formatBoost(total)}% Boost + Bonus Boost
              </strong>,
            ]}
            balance
          />
        </p>
      </div>
      <div className={styles.body}>
        <Flex
          as={List}
          container
          alignItems="stretch"
          justifyContent="center"
          className={styles.list}>
          <Flex as="li" flex={1}>
            <EvolveSelectedFriendCard active={activeCard === 0} order={0} />
          </Flex>
          <Flex as="li" container alignItems="center">
            <PlusIcon className={styles.plus} />
          </Flex>
          <Flex as="li" flex={1}>
            <EvolveSelectedFriendCard active={activeCard === 1} order={1} />
          </Flex>
          <Flex as="li" flex={1}>
            <EvolveSelectedFriendCard active={activeCard === 2} order={2} />
          </Flex>
        </Flex>
      </div>
    </BaseCard>
  );
};
